import { useCallback } from 'react';

import { Slider } from '@/components/ui/slider';

interface ResponseSliderFieldProps {
  value?: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  increment?: number | null;
  /** Optional label for the value display, e.g. "Douleur" → "Douleur niveau 0 sur 10" */
  label?: string;
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

export const ResponseSliderField = ({
  value,
  min = 0,
  max = 10,
  onChange,
  disabled,
  increment,
  label,
}: ResponseSliderFieldProps) => {
  const current = value !== undefined ? clamp(value, min, max) : min;

  const handleSliderChange = useCallback(
    (v: number | readonly number[]) => {
      const next = Array.isArray(v) ? v[0] : v;
      if (next !== undefined) onChange(next);
    },
    [onChange],
  );

  const handleDecrement = useCallback(() => {
    if (disabled) return;
    const step = increment ?? 1;
    const next = clamp(current - step, min, max);
    onChange(next);
  }, [current, disabled, increment, max, min, onChange]);

  const handleIncrement = useCallback(() => {
    if (disabled) return;
    const step = increment ?? 1;
    const next = clamp(current + step, min, max);
    onChange(next);
  }, [current, disabled, increment, max, min, onChange]);

  const labelStrong = label ? `${label} : ${current}` : `${current}`;

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <div className="flex w-full items-center justify-center rounded-1.5xl border border-blue-100 bg-blue-50 p-3.5">
        <p className="text-center text-sm font-normal leading-normal text-indigo-900">
          <span className="font-semibold">{labelStrong}</span>
          <span className="font-light"> / {max}</span>
        </p>
      </div>
      <div className="flex w-full items-center gap-4">
        <button
          type="button"
          aria-label="Diminuer"
          disabled={disabled}
          onClick={handleDecrement}
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-1.5xl border border-gray-200 bg-white shadow-[0px_1.7px_7px_0px_rgba(8,15,54,0.05)]"
        >
          <span className="-translate-y-0.5 text-3.5xl leading-none font-medium">
            −
          </span>
        </button>
        <div className="min-w-0 flex-1">
          <Slider
            min={min}
            max={max}
            step={increment ?? undefined}
            value={[current]}
            onValueChange={handleSliderChange}
            disabled={disabled}
          />
        </div>
        <button
          type="button"
          aria-label="Augmenter"
          disabled={disabled}
          onClick={handleIncrement}
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-1.5xl border border-gray-200 bg-white shadow-[0px_1.7px_7px_0px_rgba(8,15,54,0.05)]"
        >
          <span className="-translate-y-0.5 text-3.5xl leading-none font-medium">
            +
          </span>
        </button>
      </div>
    </div>
  );
};
